import { Formik, FormikConfig } from "formik";
import React, { ReactNode, useCallback, useRef, useState } from "react";
import Button from "@adsk/alloy-react-button";
import Theme from "@adsk/alloy-react-theme";
import Modal from "@adsk/alloy-react-modal";
import Checkbox, { Indeterminate } from "@adsk/alloy-react-checkbox";
import { EscapePressHandler } from "./EscapePressHandler";
import { DiscardModal } from "./DiscardModal";
import { ModalConfirmationProps } from "../types/ModalConfirmationProps";
import { useModalConfirmationState } from "../hooks/useModalConfirmationState";
import { AbsoluteCenteredSpinner } from "@adsk/acc-issues-common/components/primitive/CenteredSpinner";
import { useTranslations } from "@adsk/acc-issues-translations";
import { pickBy } from "lodash";
import { analyticsDataProperties } from "@adsk/acc-issues-core/state/redux/ducks/issuesCollection";
export interface CreationModalProps<TEntity> {
  isOpen?: boolean;
  onClose: (isSuccess?: boolean, dirtyValues?: Object) => void;
  onCreate: (newItem: TEntity, entitySubResources: any) => void;
  onDiscardConfirm?: () => void;
  onDiscardAbort?: () => void;
  analyticsData?: analyticsDataProperties;
}

interface CreationModalContentProps<TEntity, TAttributes> {
  onClose: (isSuccess?: boolean, dirtyValues?: Partial<TAttributes>) => void;
  onCreate?: (newItem: TEntity, entitySubResources: any) => void;
  title: ReactNode;
  createAnotherEntityText?: ReactNode;
  children: ReactNode;
  errors?: any;
  initialValues: TAttributes;
  persistedFieldsAfterReset?: (keyof TAttributes)[];
  createEntity: (
    attributes: TAttributes
  ) => Promise<Array<TEntity | unknown> | undefined>;
  handleValidation?: FormikConfig<TAttributes>["validate"];
  validationSchema?: FormikConfig<TAttributes>["validationSchema"];
  shouldShowConfirmationModalFn?: (attributes: TAttributes) => boolean;
  confirmationModalComponent?: React.ComponentType<ModalConfirmationProps>;
  createButtonText?: ReactNode;
  onDiscardConfirm?: () => void;
  onDiscardAbort?: () => void;
  validateOnMount?: boolean;
}

export const CreationModalContent = <TEntity, TAttributes extends object>({
  onClose,
  onCreate,
  title,
  createAnotherEntityText,
  errors,
  initialValues,
  persistedFieldsAfterReset,
  children,
  createEntity,
  handleValidation,
  validationSchema,
  confirmationModalComponent: ConfirmationModal,
  shouldShowConfirmationModalFn,
  createButtonText,
  onDiscardConfirm,
  onDiscardAbort,
  validateOnMount,
}: CreationModalContentProps<TEntity, TAttributes>) => {
  const { T } = useTranslations();
  createButtonText = createButtonText || T.translate("actions.create");
  const {
    show: isConfirmationModalOpen,
    waitForConfirmation,
    onConfirm,
    onClose: onCancel,
  } = useModalConfirmationState();

  const containerRef = useRef(null);
  const [isSubmitSuccessful, setIsSubmitSuccessful] = React.useState(false);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [shouldCreateAnotherEntity, setShouldCreateAnotherEntity] =
    useState(false);
  const handleFormSubmission = useCallback(
    async (attributes, { resetForm }) => {
      if (
        ConfirmationModal &&
        shouldShowConfirmationModalFn &&
        shouldShowConfirmationModalFn(attributes)
      ) {
        const isConfirmed = await waitForConfirmation();
        if (!isConfirmed) {
          return;
        }
      }

      try {
        const [entity, entitySubResources] =
          (await createEntity(attributes)) ?? [];

        if (!entity) {
          throw new Error("Entity failed to be created");
        }
        setIsSubmitSuccessful(true);
        onCreate?.(entity as TEntity, entitySubResources);

        if (!shouldCreateAnotherEntity) {
          onClose(true);
        } else {
          const values: any = initialValues;
          Object.keys(attributes).forEach(key => {
            if (
              persistedFieldsAfterReset &&
              persistedFieldsAfterReset.includes(key as keyof TAttributes)
            ) {
              values[key] = attributes[key];
            }
          });
          resetForm({ values });
          setIsSubmitSuccessful(false);
        }
      } catch (e: any) {
        // @TODO: we should probably bugsnag this.
        // We also shouldn't be silently ignoring errors here -- this should maybe trigger a notification.
        // eslint-disable-next-line no-console
        console.error(e);
      }
    },
    [
      shouldCreateAnotherEntity,
      createEntity,
      onClose,
      onCreate,
      ConfirmationModal,
      waitForConfirmation,
      shouldShowConfirmationModalFn,
      initialValues,
      persistedFieldsAfterReset,
    ]
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleFormSubmission}
      validate={handleValidation}
      validationSchema={validationSchema}
      validateOnMount={validateOnMount}
    >
      {({
        handleSubmit,
        isSubmitting,
        dirty,
        values,
        setFieldError,
        errors: formikErrors,
      }) => {
        if (errors) {
          Object.entries(errors).forEach(([errorKey, errorValue]) => {
            if ((formikErrors as any)[errorKey] !== errorValue) {
              setFieldError(errorKey, errorValue as string);
            }
          });
        }
        const onCloseAttempt = () =>
          dirty ? setIsDiscardModalOpen(true) : onClose();

        // hasSubmittedForm allows us to keep showing the loader while the modal fades away.
        // Using formik's `submitCount` doesn't work because that includes
        // attempted submissions that ran into errors.
        const hasSubmittedForm = isSubmitting || isSubmitSuccessful;
        const handleDiscard = () => {
          const dirtyValues = pickBy(
            values,
            //@ts-ignore
            (value, field) => initialValues[field] !== value
          );
          onClose(false, dirtyValues);
        };
        return (
          <div ref={containerRef}>
            <Modal.Header>
              {title} <Modal.Close onClick={onCloseAttempt} />
            </Modal.Header>
            <EscapePressHandler onEscapePress={onCloseAttempt} />
            {hasSubmittedForm && (
              <AbsoluteCenteredSpinner zIndex={Theme.zIndex.overlay} />
            )}
            <Modal.Body
              style={{
                maxHeight: "70vh",
                padding: "0px 24px 16px",
                opacity: hasSubmittedForm ? 0 : 1,
              }}
            >
              {children}
              <DiscardModal
                isOpen={isDiscardModalOpen}
                onDiscard={handleDiscard}
                closeDiscardModal={() => setIsDiscardModalOpen(false)}
                onDiscardConfirm={onDiscardConfirm}
                onDiscardAbort={onDiscardAbort}
              />
              {ConfirmationModal && (
                <ConfirmationModal
                  show={isConfirmationModalOpen}
                  onConfirm={onConfirm}
                  onClose={onCancel}
                />
              )}
            </Modal.Body>
            <Modal.Footer
              style={{
                justifyContent: createAnotherEntityText
                  ? "space-between"
                  : "flex-end",
              }}
            >
              {createAnotherEntityText && (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    checked={shouldCreateAnotherEntity}
                    onChange={(value: boolean | Indeterminate) =>
                      setShouldCreateAnotherEntity(!!value)
                    }
                  />
                  <span style={{ paddingLeft: 8 }}>
                    {createAnotherEntityText}
                  </span>
                </div>
              )}
              <div>
                <Button
                  disabled={hasSubmittedForm}
                  variant={Button.VARIANTS.TERTIARY}
                  onClick={onCloseAttempt}
                >
                  {T.translate("actions.cancel")}
                </Button>
                <Button
                  disabled={hasSubmittedForm}
                  variant={Button.VARIANTS.PRIMARY}
                  style={{ marginLeft: 16 }}
                  onClick={() => handleSubmit()}
                >
                  {createButtonText}
                </Button>
              </div>
            </Modal.Footer>
          </div>
        );
      }}
    </Formik>
  );
};

export const CreationModalWrapper = ({
  isOpen,
  children,
}: {
  isOpen?: boolean;
  children: ReactNode;
}) => {
  return (
    <Modal
      data-testId="creation-modal"
      open={isOpen}
      size={Modal.SIZES.MEDIUM}
      height={"inherit"}
      shouldCloseOnEsc={false}
    >
      {isOpen && children}
    </Modal>
  );
};
